import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import authService from '../services/auth';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

const AccountDisabledMessage = () => {
  return (
    <div className="w-full min-w-0 max-w-md space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto h-16 w-16 relative">
          <div className="absolute inset-0 bg-amber-500/20 dark:bg-amber-400/10 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
          <div className="relative h-16 w-16 bg-gradient-to-br from-amber-400 to-amber-600 dark:from-amber-500 dark:to-amber-700 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/25">
            <ShieldCheck className="h-8 w-8 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
          Awaiting Approval
        </h2>
        <p className="mt-3 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
          Your registration is complete
        </p>
      </div>

      {/* Info card */}
      <div className="space-y-4 rounded-lg border border-light-border-primary bg-light-bg-primary p-4 dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mt-2" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            Your account has been successfully created but requires administrator approval before you can access the system.
          </p>
        </div>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mt-2" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            You will be able to sign in once an administrator reviews and activates your account.
          </p>
        </div>
      </div>

      {/* Status badge */}
      <div className="flex justify-center">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50">
          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 animate-pulse" />
          Pending administrator review
        </span>
      </div>

      {/* Back to login */}
      <Link
        to="/login"
        className="w-full inline-flex items-center justify-center px-4 py-3 border border-light-border-primary dark:border-dark-border-primary text-sm font-medium rounded-lg text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Sign In
      </Link>
    </div>
  );
};

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [accountDisabled, setAccountDisabled] = useState(false);
  const [processed, setProcessed] = useState(false);
  const navigate = useNavigate();
  const { setUser, loadUserPermissions } = useAuth();

  useEffect(() => {
    // Avoid double-processing
    if (processed) return;

    const processOAuthCallback = async () => {
      try {
        // Check for OAuth errors from provider
        const oauthError = searchParams.get('error');
        if (oauthError) {
          if (oauthError === 'account_disabled') {
            setAccountDisabled(true);
            setProcessed(true);
            return;
          }
          throw new Error(`OAuth error: ${oauthError}`);
        }

        // Read token from URL params
        const accessToken = searchParams.get('accessToken');

        if (!accessToken) {
          throw new Error('No access token received from OAuth provider');
        }

        // Store access token and exchange for refresh token cookie
        await authService.handleOAuthCallback(accessToken);

        // Load current user and update context directly
        try {
          const profileResponse = await authService.getCurrentUser();
          if (profileResponse?.user) {
            setUser(profileResponse.user);
            loadUserPermissions(profileResponse.user);
          } else {
            setUser(null);
            loadUserPermissions(null);
          }
        } catch (e) {
          setUser(null);
          loadUserPermissions(null);
        }

        setProcessed(true);
        // Redirect to home
        navigate('/', { replace: true });
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError(error.message || 'Authentication failed');
        setProcessed(true);

        // Redirect to login on error with a short delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    processOAuthCallback();
  }, [searchParams, navigate, processed, setUser, loadUserPermissions]);

  const content = accountDisabled ? (
    <AccountDisabledMessage />
  ) : error ? (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center dark:border-red-800 dark:bg-red-900/20 sm:p-6">
      <h2 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">Authentication Error</h2>
      <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
      <p className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">Redirecting to login page...</p>
    </div>
  ) : (
    <div className="text-center flex flex-col items-center">
      <LoadingSpinner size="lg" className="mb-4" />
      <h2 className="text-xl font-medium text-light-text-primary dark:text-dark-text-primary mb-2">Completing Authentication</h2>
      <p className="text-light-text-secondary dark:text-dark-text-secondary">Please wait while we complete the authentication process...</p>
    </div>
  );

  return (
    <div className="flex w-full min-w-0 flex-1 items-center justify-center">
      <title>Signing in | Talent Service</title>
      <div className="w-full min-w-0 max-w-md">
        {content}
      </div>
    </div>
  );
};

export default OAuthCallback;